import * as d3 from 'd3'
import { max, repeat } from 'ramda'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useStyles from './ScoresHistogram.styles'

const THRESHOLD_MIN_VALUE = 0.01
const THRESHOLD_STEP_VALUE = 0.01
const THRESHOLD_MAX_VALUE = 1000

/**
 * Reverse slice.
 *
 * Please note: this is unefficient and untest. Use with care.
 *
 * @param start Right index (inclusive)
 * @param end Left index (inclusive)
 * @param arr An array
 * @returns A slice of `arr` from right to left
 */
const sliceR = (start: number, end: number, arr: unknown[]) => {
  const arr2 = new Array(start - end)

  for (let i = start; i >= end; i -= 1) {
    arr2.push(arr[i])
  }

  return arr2
}

const mkBin = <T,>(arr: T[], x0?: number, x1?: number): d3.Bin<T, number> => {
  // any is required because we cannot create an array
  // with `x0` and `x1` properties in one line

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const bin: any = arr

  bin.x0 = x0
  bin.x1 = x1

  return bin
}

/**
 * Return an array of ticks based on scores lenght, min and max.
 *
 * The returned array has following properties:
 *
 * - its lenght is `len(scores)`
 * - width of each tick is `(max(scores) - min(scores)) / len(scores)`
 * - first tick starts at `min(scores)`import { useDispatch, useStore } from '../../store/store'
 */
const getTicks = (scores: number[]) => {
  const nBins = scores.length
  const xMin = d3.min(scores) || 0
  const xMax = d3.max(scores) || 10
  const binSize = (xMax - xMin) / nBins // `nBins` may be zero when `scores` is empty
  return repeat(0, nBins).map((_, i) => xMin + binSize * i) // generate an array of positions for bins
}

/**
 * Set last bin `x1` property to `maximum` if last bin exists.
 *
 * @see https://dev.to/kevinlien/d3-histograms-and-fixing-the-bin-problem-4ac5
 */
const fixLastBinSize = (bins: d3.Bin<number, number>[], maximum?: number) => {
  if (bins[bins.length - 1]) {
    // eslint-disable-next-line no-param-reassign
    bins[bins.length - 1].x1 = maximum
  }
}

type ScoresHistogramProps = {
  anomalyScores: number[]
  normalScores: number[]
  thresholdX: number
  svgWid?: number
  svgHei?: number
  onThresholdChanged?: (threshold: number, simulationMode: string) => void
}

export const ScoresHistogram = ({
  anomalyScores,
  normalScores,
  thresholdX,
  svgWid,
  svgHei,
  onThresholdChanged,
}: ScoresHistogramProps) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const [customThreshold, setCustomThreshold] = useState(thresholdX)

  const formatValue = (value: number | string) => {
    if (typeof value === 'number') {
      return value.toFixed(2) || value
    }
    return value
  }

  const floorValue = (value: number | string) => {
    if (typeof value === 'number') {
      return Math.floor(value * 100) / 100 || value
    }
    return value
  }

  // Update customThreshold when thresholdX changes
  useEffect(() => {
    setCustomThreshold(Number(floorValue(thresholdX)))
  }, [thresholdX])

  const chartRef = useRef<SVGSVGElement>(null)

  const svgWidth = svgWid || 350
  const svgHeight = svgHei || 350

  const margin = { top: 10, right: 10, bottom: 60, left: 25 }
  const width = svgWidth - margin.left - margin.right
  const height = svgHeight - margin.top - margin.bottom

  const gutter = 8
  const estimatedLabelSize = 30

  const maxScore = d3.max([...anomalyScores, ...normalScores])
  const maxThreshold = maxScore !== undefined ? maxScore : THRESHOLD_MAX_VALUE
  const minScore = d3.min([...anomalyScores, ...normalScores])
  const minThreshold = minScore !== undefined ? minScore : THRESHOLD_MIN_VALUE

  function keyPress(event: KeyboardEvent) {
    const { code } = event
    if (code === 'ArrowRight' || code === 'ArrowLeft') {
      showButton('buttonLoadSimulationPageDivId')
    }
  }

  function showButton(divId: string) {
    const buttonDiv = document.getElementById(divId)
    if (buttonDiv) {
      buttonDiv.style.display = 'unset'
    }
  }

  function hideButton(divId: string) {
    const buttonDiv = document.getElementById(divId)
    if (buttonDiv) {
      buttonDiv.style.display = 'none'
    }
  }

  function removeButtonEventListener() {
    document.removeEventListener('keydown', keyPress, false)
  }

  useEffect(() => {
    d3.select(chartRef.current).selectAll('*').remove() // avoid multiple appends

    const scores = [...anomalyScores, ...normalScores]
    const xMin = d3.min(scores) || 0
    const xMax = d3.max(scores) || 10

    // padding (like flooring and ceiling) breaks histogram first and last bar width.
    // Thus, we use real domain values to avoid this.
    // Exception: when xMin === xMax the scale is degenerate and all coords become NaN,
    // so we add a small epsilon to keep a valid domain.
    const epsilon = xMin === xMax ? 0.01 : 0
    const xMinDomain = xMin - epsilon
    const xMaxDomain = xMax + epsilon

    const xAxis = d3
      .scaleLinear()
      .domain([xMinDomain, xMaxDomain])
      .range([0, width])
    const yAxis = d3.scaleLinear().range([height, 0])

    /** get data x0 if available, else 0  */
    const x0 = (d: d3.Bin<number, number>) => (d.x0 ? xAxis(d.x0) : 0)

    /** get data x1 if available, else 0 */
    const x1 = (d: d3.Bin<number, number>) => (d.x1 ? xAxis(d.x1) : 0)

    /** get data height if available, else 0 */
    const yd0 = (d: d3.Bin<number, number>) => (d[0] ? yAxis(d[0]) : height) // default to height, i.e., svg bottom

    // create chart structures

    const anomalyHistogram = d3
      .bin()
      .domain(xAxis.domain() as [number, number])
      .thresholds(getTicks(anomalyScores))

    const normalHistogram = d3
      .bin()
      .domain(xAxis.domain() as [number, number])
      .thresholds(getTicks(normalScores))

    const anomalyBins = anomalyHistogram(anomalyScores)
    const normalBins = normalHistogram(normalScores)
    fixLastBinSize(normalBins, d3.max(normalScores)) // otherwise, the last `x1` is equal to `xMaxDomain`

    const binZero = mkBin([])

    // cumulate bins to the right
    const cumulatedAnomalyBins: d3.Bin<number, number>[] = anomalyBins.map(
      (_, i) =>
        anomalyBins
          .slice(0, i + 1) // last index is exclusive, thus +1
          .reduce(
            (a, b) => mkBin([Number(a) + Number(b.length)], b.x0, b.x1),
            binZero
          )
    )

    // cumulate bins to the left
    const cumulatedNormalBins: d3.Bin<
      number,
      number
    >[] = normalBins.map((_, i) =>
      sliceR(normalBins.length - 1, i, normalBins).reduce(
        (a, b) => mkBin([Number(a) + Number(b.length)], b.x0, b.x1),
        binZero
      )
    )

    // Set yAxis according to number of anomaly samples,
    // if there are no anomaly samples, use number of normal samples instead
    // if both are zero, use 10 as default to avoid degenerate scale
    const yAxisAnomaly = d3.max(cumulatedAnomalyBins.map((x) => x[0])) || 0
    const yAxisNormal = d3.max(cumulatedNormalBins.map((x) => x[0])) || 10
    yAxis.domain([0, yAxisAnomaly > 0 ? yAxisAnomaly : yAxisNormal]) // yAxis domain depends on bins

    // create svg with props
    const svg = d3
      .select(chartRef.current)
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .attr('viewBox', `0 0 ${svgWidth} ${svgHeight}`)
      .attr('class', classes.chartScoresHistogram)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    // append xAxis
    svg
      .append('g')
      .attr('transform', `translate(0, ${height})`)
      .call(d3.axisBottom(xAxis))

    // append xAxis label
    svg
      .append('text')
      .attr('class', classes.xAxisLabel)
      .attr('x', width / 2)
      .attr('y', height + 45) // add some spacing between axis and label
      .text(t('Anomaly score'))

    // append yAxis
    svg.append('g').call(d3.axisLeft(yAxis))

    // append anomaly bars
    svg
      .selectAll('anomalyRect')
      .data(cumulatedAnomalyBins)
      .enter()
      .append('rect')
      .attr('x', 1) // add some spacing between bars
      .attr('transform', (d) => `translate(${x0(d)} , ${yd0(d)})`)
      .attr('width', (d) => max(x1(d) - x0(d) - 1, 0))
      .attr('height', (d) => height - yd0(d))
      .attr('class', classes.anomalyBar)

    // append normal bars
    svg
      .selectAll('normalRect')
      .data(cumulatedNormalBins)
      .enter()
      .append('rect')
      .attr('x', 1) // add some spacing between bars
      .attr('transform', (d) => `translate(${x0(d)} , ${yd0(d)})`)
      .attr('width', (d) => max(x1(d) - x0(d) - 1, 0))
      .attr('height', (d) => height - yd0(d))
      .attr('class', classes.normalBar)

    // append threshold line
    svg
      .append('line')
      .attr('x1', xAxis(customThreshold))
      .attr('x2', xAxis(customThreshold))
      .attr('y1', 0)
      .attr('y2', height) // full height line
      .attr('class', classes.threshold)

    // append threshold text
    svg
      .append('text')
      .attr(
        'x',
        // when text is close to the right side of the chart and may be cut off,
        // move it to the left of the threshold line
        xAxis(customThreshold) + gutter > width - estimatedLabelSize
          ? xAxis(customThreshold) - gutter - estimatedLabelSize
          : xAxis(customThreshold) + gutter
      )
      .attr('y', 20) // 20px from top
      .attr('class', classes.thresholdText)
      .text(`${customThreshold}`)
  }, [
    anomalyScores,
    classes.anomalyBar,
    classes.chartScoresHistogram,
    classes.normalBar,
    classes.thresholdText,
    classes.threshold,
    classes.xAxisLabel,
    height,
    margin.bottom,
    margin.left,
    margin.right,
    margin.top,
    normalScores,
    customThreshold,
    width,
    t,
    svgWidth,
    svgHeight,
  ])

  return (
    <div>
      <svg ref={chartRef} />
      <div className={classes.thresholdSimulationDiv}>
        <output className={classes.thresholdSimulationText}>
          {t('Threshold')}
        </output>
        <input
          className={classes.thresholdSimulation}
          type="range"
          onChange={(event) => {
            setCustomThreshold(Number(floorValue(event.target.value)))
            const inputTextThreshold = document.getElementById(
              'thresholdSimultationTxt'
            )
            if (inputTextThreshold) {
              const i = inputTextThreshold as HTMLInputElement
              i.value = String(event.target.value)
            }
          }}
          onMouseUp={(e) => {
            e.stopPropagation()
            hideButton('buttonLoadSimulationPageDivId')
            if (onThresholdChanged) {
              onThresholdChanged(customThreshold, 'anomaly_threshold')
            }
          }}
          onMouseDown={(e) => {
            e.stopPropagation()
            hideButton('buttonLoadSimulationPageDivId')
          }}
          onFocus={(e) => {
            document.addEventListener('keydown', keyPress, false)
          }}
          onBlur={(e) => {
            removeButtonEventListener()
          }}
          step={THRESHOLD_STEP_VALUE}
          value={customThreshold}
          min={formatValue(minThreshold)}
          max={formatValue(maxThreshold)}
          title={t('Slide to simulate new threshold value.')}
        />
        <input
          id="thresholdSimultationTxt"
          className={classes.thresholdSimulationTxt}
          type="number"
          step={THRESHOLD_STEP_VALUE}
          defaultValue={floorValue(thresholdX)}
          value={customThreshold}
          onFocus={(e) => {
            showButton('buttonLoadSimulationPageDivId')
          }}
          onChange={(event) => {
            setCustomThreshold(Number(floorValue(event.target.value)))
          }}
          onBlur={(event) => {
            event.stopPropagation()
            if (onThresholdChanged) {
              onThresholdChanged(
                d3.max([customThreshold, THRESHOLD_MIN_VALUE]) ||
                  THRESHOLD_MIN_VALUE,
                'anomaly_threshold'
              )
            }
          }}
          min={formatValue(minThreshold)}
          max={formatValue(maxThreshold)}
        />
      </div>
    </div>
  )
}
